import { useEffect, useState } from "react";
import "./App.css";
import {
  NAV,
  affiliations,
  appointments,
  cvPublications,
  education,
  projects,
  publications,
  researchInterests,
  selectedResearch,
  socialLinks,
  technicalSkills,
} from "./data/siteData";

const VALID_ROUTES = new Set(NAV.map((item) => item.id));

function routeFromHash() {
  const route = window.location.hash.replace(/^#\/?/, "").toLowerCase();
  return VALID_ROUTES.has(route) ? route : "about";
}

export default function App() {
  const [active, setActive] = useState(routeFromHash);

  useEffect(() => {
    const onHashChange = () => setActive(routeFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    const label = NAV.find((item) => item.id === active)?.label || "About";
    document.title = active === "about" ? "Kennedy Adriko — AI for Earth Observation" : `${label} — Kennedy Adriko`;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [active]);

  return (
    <div className="site">
      <nav className="nav" aria-label="Primary navigation">
        <a className="nav-name" href="#about">Kennedy Adriko</a>
        <ul className="nav-links">
          {NAV.map((item) => (
            <li key={item.id}>
              <a
                className={`nav-btn${active === item.id ? " active" : ""}`}
                href={`#${item.id}`}
                aria-current={active === item.id ? "page" : undefined}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <main>
        {active === "about" && <About />}
        {active === "research" && <Research />}
        {active === "projects" && <Projects />}
        {active === "cv" && <CV />}
      </main>
    </div>
  );
}

function About() {
  return (
    <>
      <section className="hero">
        <p className="hero-label">PhD Researcher · SDL AI and ML for Remote Sensing · JSC</p>
        <h1>AI for<br /><em>Earth Observation</em></h1>
        <p className="hero-bio hero-bio-lead">
          I am a PhD researcher at the Jülich Supercomputing Centre working on efficient and scalable AI for Earth observation. My research spans geospatial foundation models, multimodal data fusion, neural compression, and high-performance computing.
        </p>
        <p className="hero-bio">
          I am pursuing my PhD in Electrical and Computer Engineering through the University of Iceland and previously completed the Erasmus Mundus Copernicus Master in Digital Earth, specialising in Geodata Science.
        </p>
        <div className="hero-links">
          {socialLinks.map((link) => (
            <a
              className="pill"
              href={link.href}
              key={link.label}
              target={link.href.startsWith("http") ? "_blank" : undefined}
              rel={link.href.startsWith("http") ? "noreferrer" : undefined}
            >
              {link.label}
            </a>
          ))}
        </div>
      </section>

      <section className="home-section" aria-labelledby="selected-research-heading">
        <div className="section-header home-section-header">
          <h2 id="selected-research-heading">Selected research</h2>
          <p>Representative work across sensing, foundation models, and scalable geospatial AI</p>
        </div>
        <div className="selected-grid">
          {selectedResearch.map((item) => (
            <article className="selected-card" key={item.title}>
              <div className="selected-eyebrow">{item.eyebrow}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
              <div className="pub-tags compact-tags">
                {item.tags.map((tag) => <span className="tag" key={tag}>{tag}</span>)}
              </div>
              <div className="selected-links">
                {item.links.map((link) => (
                  <a
                    className="pill"
                    href={link.href}
                    key={link.label}
                    target={link.href.startsWith("http") ? "_blank" : undefined}
                    rel={link.href.startsWith("http") ? "noreferrer" : undefined}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section" aria-labelledby="research-interests-heading">
        <div className="section-header home-section-header"><h2 id="research-interests-heading">Research interests</h2></div>
        <div className="card-grid">
          {researchInterests.map((item) => (
            <div className="proj-card" key={item.title}>
              <div className="proj-icon">{item.icon}</div>
              <div className="proj-name">{item.title}</div>
              <div className="proj-desc">{item.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="affil-row">
        {affiliations.map((affiliation) => (
          <div className="affil-item" key={affiliation}>
            <span className="affil-dot" />
            {affiliation}
          </div>
        ))}
      </div>
    </>
  );
}

function Research() {
  return (
    <>
      <div className="section-header">
        <h2>Research &amp; publications</h2>
        <p>Peer-reviewed work and selected research in progress</p>
      </div>
      <div className="pub-list">
        {publications.map((publication) => (
          <article className="pub-card" key={publication.title}>
            <div className="pub-meta">
              <span>{publication.venue}</span>
              {publication.status && (
                <span className={`pub-badge${publication.draft ? " draft" : ""}`}>{publication.status}</span>
              )}
            </div>
            <div className="pub-title">{publication.title}</div>
            <div className="pub-authors">{publication.authors}</div>
            <div className="pub-abstract"><strong>Research note:</strong> {publication.summary}</div>
            <div className="pub-tags">
              {publication.tags.map((tag) => <span className="tag" key={tag}>{tag}</span>)}
            </div>
            {publication.links.length > 0 && (
              <div className="pub-actions">
                {publication.links.map((link) => (
                  <a className="pill" href={link.href} target="_blank" rel="noreferrer" key={link.label}>{link.label}</a>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </>
  );
}

function Projects() {
  return (
    <>
      <div className="section-header">
        <h2>Projects &amp; Affiliations</h2>
        <p>Research projects and institutional affiliations</p>
      </div>
      <div className="card-grid two-col">
        {projects.map((project) => (
          <div className="proj-card" key={project.name}>
            <div className="proj-icon">{project.icon}</div>
            <div className="proj-name">{project.name}</div>
            <div className="proj-desc">{project.desc}</div>
            <div className="proj-foot">
              <a href={project.link} target="_blank" rel="noreferrer">{project.linkLabel}</a>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function CVEntry({ entry }) {
  return (
    <div className="cv-entry">
      <div className="cv-year">{entry.year}</div>
      <div>
        <div className="cv-title">{entry.title}</div>
        <div className="cv-sub">{entry.sub}</div>
        {entry.detail && <div className="cv-sub cv-detail">{entry.detail}</div>}
      </div>
    </div>
  );
}

function CV() {
  return (
    <>
      <div className="section-header">
        <h2>Curriculum vitae</h2>
        <p>Academic and research history</p>
      </div>
      <div className="cv-grid">
        <div className="cv-block">
          <h3>Appointments &amp; research</h3>
          {appointments.map((entry) => <CVEntry entry={entry} key={`${entry.year}-${entry.title}`} />)}
        </div>

        <div className="cv-block">
          <h3>Education</h3>
          {education.map((entry) => <CVEntry entry={entry} key={`${entry.year}-${entry.title}`} />)}
        </div>

        <div className="cv-block">
          <h3>Selected publications</h3>
          {cvPublications.map((publication) => (
            <div className="cv-entry" key={publication.title}>
              <div className="cv-year">{publication.venue.split("·").pop().trim()}</div>
              <div>
                <div className="cv-title">{publication.title}</div>
                <div className="cv-sub">{publication.venue.split("·")[0].trim()}</div>
                <div className="cv-sub cv-detail">
                  <a href={publication.links[0].href} target="_blank" rel="noreferrer">View paper ↗</a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="cv-block">
          <h3>Technical skills</h3>
          <div className="cv-skills skills-pad">
            {technicalSkills.map((skill) => <span className="tag" key={skill}>{skill}</span>)}
          </div>
        </div>
      </div>
    </>
  );
}
